import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { usersAPI, UserDataResponse } from '../api/users';

interface UserDataContextType {
  userData: UserDataResponse | null;
  loading: boolean;
  refreshUserData: () => Promise<void>;
  // 便捷访问器
  profile: UserDataResponse['profile'] | null;
  stats: UserDataResponse['stats'] | null;
  membership: UserDataResponse['membership'] | null;
  referral: UserDataResponse['referral'] | null;
}

const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within UserDataProvider');
  }
  return context;
};

interface UserDataProviderProps {
  children: ReactNode;
}

export const UserDataProvider: React.FC<UserDataProviderProps> = ({ children }) => {
  const [userData, setUserData] = useState<UserDataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  
  // 重要：缓存键必须包含用户标识，确保不同用户的数据不会混淆
  const getStorageKey = useCallback((): string => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return 'user_data_cache_no_user';
    }
    // 使用token的前16个字符作为用户标识（安全且唯一）
    const userIdentifier = token.substring(0, 16);
    return `user_data_cache_${userIdentifier}`;
  }, []);

  // 刷新用户数据
  const refreshUserData = useCallback(async () => {
    try {
      setLoading(true);
      // 未登录时不触发刷新，避免无意义的401
      const token = localStorage.getItem('access_token');
      if (!token) {
        // 清除缓存，因为用户已登出
        setUserData(null);
        return;
      }
      
      const STORAGE_KEY = getStorageKey();
      
      // 尝试从缓存恢复数据作为回退（但只在有token时）
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.data) {
            // 检查缓存是否过期（5分钟）
            const cacheAge = Date.now() - (parsed.cached_at || 0);
            if (cacheAge < 300000) { // 5分钟内有效
              setUserData(parsed.data as UserDataResponse);
            }
          }
        }
      } catch (e) {
        console.warn('读取用户数据缓存失败:', e);
      }
      
      // 调用统一API获取所有用户数据，添加超时保护
      const dataPromise = usersAPI.getUserData();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('请求超时')), 25000);
      });
      
      let data: UserDataResponse;
      try {
        data = await Promise.race([dataPromise, timeoutPromise]) as UserDataResponse;
        setUserData(data);
      } catch (error: any) {
        // 超时或请求失败时，使用缓存数据
        if (error.message === '请求超时' || error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          console.warn('获取用户数据超时或失败，使用缓存数据:', error.message);
          const STORAGE_KEY = getStorageKey();
          try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && parsed.data) {
                data = parsed.data as UserDataResponse;
                setUserData(data);
                // 不更新缓存，保持旧缓存
                return;
              }
            }
          } catch (e) {
            console.warn('读取用户数据缓存失败:', e);
          }
          // 如果缓存也没有，保持现有数据或使用默认值
          throw error;
        } else {
          throw error;
        }
      }
      
      // 缓存最新的数据，便于下次启动或请求失败时回退
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          data,
          cached_at: Date.now(),
        }));
      } catch (e) {
        // 忽略localStorage错误（比如隐私模式）
        console.warn('缓存用户数据失败:', e);
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401) {
        console.warn('刷新用户数据失败(401): 未登录或令牌过期');
        // 401时清除缓存和状态
        setUserData(null);
      } else if (status === 503) {
        console.warn('刷新用户数据失败(503): 服务暂时不可用，使用缓存数据');
        // 503 时不清除数据，保持显示缓存数据
        // 不显示错误，因为这是临时性的服务不可用
      } else {
        console.error('刷新用户数据失败:', error);
      }
      // 不抛出错误，保持应用稳定运行
      // 若已有旧数据，保持显示；若没有，则尝试从缓存读取
      setUserData((prevData) => {
        if (!prevData) {
          try {
            const STORAGE_KEY = getStorageKey();
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && parsed.data) {
                return parsed.data as UserDataResponse;
              }
            }
          } catch (e) {
            console.warn('读取用户数据缓存失败:', e);
          }
        }
        return prevData;
      });
    } finally {
      setLoading(false);
    }
  }, [getStorageKey]);

  // 组件挂载时初始化数据
  useEffect(() => {
    // 未登录不触发刷新，避免无意义的401噪音
    const token = localStorage.getItem('access_token');
    if (token) {
      // 检查是否是刚登录（通过检查 sessionStorage 标记）
      const isJustLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
      if (isJustLoggedIn) {
        // 清除所有旧缓存，强制刷新
        try {
          const STORAGE_KEY = getStorageKey();
          localStorage.removeItem(STORAGE_KEY);
          // 同时清除所有可能的旧缓存键（兼容旧版本）
          localStorage.removeItem('user_data_cache');
          // 清除所有以 user_data_cache_ 开头的缓存
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('user_data_cache_')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
          console.log(`UserDataContext: 已清除 ${keysToRemove.length + 1} 个用户数据缓存`);
          // 重置状态
          setUserData(null);
        } catch (e) {
          console.warn('清除用户数据缓存失败:', e);
        }
        // 清除标记
        sessionStorage.removeItem('justLoggedIn');
      }
      refreshUserData();
    } else {
      // 未登录，清除缓存和状态
      setUserData(null);
    }
  }, [refreshUserData, getStorageKey]);

  // 便捷访问器
  const profile = userData?.profile || null;
  const stats = userData?.stats || null;
  const membership = userData?.membership || null;
  const referral = userData?.referral || null;

  const value: UserDataContextType = {
    userData,
    loading,
    refreshUserData,
    profile,
    stats,
    membership,
    referral,
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};

